/**
 * Command Schema for AI Presentation Layer Editing
 *
 * Defines the structure of commands that AI can output to directly
 * manipulate MMOs (Mixed Media Objects) in the presentation layer.
 */

import Color from '../../../model/Color';

/**
 * RGBA color representation for commands
 */
export interface CommandColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

/**
 * Frame/position representation
 */
export interface CommandFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Target specification for identifying MMOs or screens
 */
export interface CommandTarget {
  type: 'mmo' | 'screen';
  /** Index of the MMO in the screen's mixedMediaObjects array */
  mmoIndex?: number;
  /** Content ID from the content layer mapping */
  contentId?: string;
  /** Natural language description for AI to match (e.g., "the title", "the image on the left") */
  description?: string;
  /** Screen index for screen-level operations */
  screenIndex?: number;
}

/**
 * Individual operation to perform on a target
 */
export interface CommandOperation {
  /** Property path (e.g., "font.fontSize", "frame.x", "color") */
  property: string;
  /** The new value to set */
  value: any;
  /** If true, value is added to the current value (e.g., +10 for relative positioning) */
  relative?: boolean;
}

/**
 * Command types for different operations
 */
export type CommandType = 'mmo_edit' | 'screen_edit' | 'create' | 'delete' | 'reorder' | 'screen_add' | 'screen_delete' | 'screen_duplicate';

/**
 * Shape types available for creation
 */
export type ShapeType =
  | 'rectangle'
  | 'roundedRect'
  | 'circle'
  | 'triangle'
  | 'arrow'
  | 'diamond'
  | 'star'
  | 'hexagon';

/**
 * MMO types for creation commands
 */
export type MMOType = 'text' | 'shape' | 'image' | 'video';

/**
 * Base command interface
 */
export interface BaseCommand {
  type: CommandType;
  target: CommandTarget;
}

/**
 * Command to edit an existing MMO's properties
 */
export interface MMOEditCommand extends BaseCommand {
  type: 'mmo_edit';
  operations: CommandOperation[];
}

/**
 * Command to edit screen-level properties (e.g., background color)
 */
export interface ScreenEditCommand extends BaseCommand {
  type: 'screen_edit';
  operations: CommandOperation[];
}

/**
 * Command to create a new MMO
 */
export interface CreateCommand extends BaseCommand {
  type: 'create';
  mmoType: MMOType;
  properties: {
    // Text properties
    text?: string;
    font?: {
      fontName?: string;
      fontSize?: number;
    };
    textColor?: CommandColor;
    textAlignment?: 'left' | 'center' | 'right';

    // Shape properties
    shapeType?: ShapeType;
    color?: CommandColor;

    // Gradient
    gradient?: {
      type: 'linear' | 'radial';
      angle?: number;
      stops: { color: string; offset: number }[];
    };

    // Common properties
    frame?: Partial<CommandFrame>;
    borderColor?: CommandColor;
    borderWidth?: number;
    cornerRadius?: number;
  };
}

/**
 * Command to delete an MMO
 */
export interface DeleteCommand extends BaseCommand {
  type: 'delete';
}

/**
 * Command to reorder an MMO (bring to front, send to back)
 */
export interface ReorderCommand extends BaseCommand {
  type: 'reorder';
  direction: 'front' | 'back' | 'forward' | 'backward';
}

/**
 * Command to add a new screen
 */
export interface ScreenAddCommand extends BaseCommand {
  type: 'screen_add';
  /** Optional index to insert at (defaults to end) */
  insertAt?: number;
}

/**
 * Command to delete a screen
 */
export interface ScreenDeleteCommand extends BaseCommand {
  type: 'screen_delete';
}

/**
 * Command to duplicate a screen
 */
export interface ScreenDuplicateCommand extends BaseCommand {
  type: 'screen_duplicate';
}

/**
 * Union type for all command types
 */
export type MMOCommand =
  | MMOEditCommand
  | ScreenEditCommand
  | CreateCommand
  | DeleteCommand
  | ReorderCommand
  | ScreenAddCommand
  | ScreenDeleteCommand
  | ScreenDuplicateCommand;

/**
 * Result of command execution
 */
export interface CommandResult {
  success: boolean;
  error?: string;
  /** The ID of the affected MMO (for create/edit operations) */
  mmoId?: string;
  /** The screen index where the operation was performed */
  screenIndex?: number;
}

/**
 * Batch command for multiple operations
 */
export interface BatchCommand {
  commands: MMOCommand[];
}

/**
 * Screen context passed to AI for command generation
 */
export interface ScreenContext {
  screenIndex: number;
  screenSize: {
    width: number;
    height: number;
  };
  background: CommandColor;
  elements: ScreenElement[];
}

/**
 * Element representation for screen context
 */
export interface ScreenElement {
  index: number;
  type: 'text' | 'shape' | 'image' | 'video' | 'pdf' | 'gallery' | 'web';
  contentId?: string;
  description?: string;
  text?: string;
  frame: CommandFrame;
  fontSize?: number;
  fontName?: string;
  textColor?: CommandColor;
  color?: CommandColor;
  shapeType?: ShapeType;
}

/**
 * Validate a command structure
 */
export function validateCommand(command: MMOCommand): { valid: boolean; error?: string } {
  if (!command) {
    return { valid: false, error: 'Command is null or undefined' };
  }

  if (!command.type) {
    return { valid: false, error: 'Command type is required' };
  }

  if (!command.target) {
    return { valid: false, error: 'Command target is required' };
  }

  // Normalize LLM aliases before validation
  const typeAliases: Record<string, CommandType> = {
    'mmo_add': 'create',
    'add': 'create',
    'mmo_create': 'create',
    'mmo_delete': 'delete',
    'mmo_reorder': 'reorder',
    'add_slide': 'screen_add',
    'add_screen': 'screen_add',
    'delete_slide': 'screen_delete',
    'delete_screen': 'screen_delete',
    'remove_slide': 'screen_delete',
    'duplicate_slide': 'screen_duplicate',
    'copy_slide': 'screen_duplicate',
    'copy_screen': 'screen_duplicate',
  };
  if (typeAliases[command.type]) {
    (command as any).type = typeAliases[command.type];
  }

  const validTypes: CommandType[] = ['mmo_edit', 'screen_edit', 'create', 'delete', 'reorder', 'screen_add', 'screen_delete', 'screen_duplicate'];
  if (!validTypes.includes(command.type)) {
    return { valid: false, error: `Invalid command type: ${command.type}` };
  }

  if (command.type === 'mmo_edit' || command.type === 'screen_edit') {
    const editCmd = command as MMOEditCommand | ScreenEditCommand;
    if (!editCmd.operations || !Array.isArray(editCmd.operations) || editCmd.operations.length === 0) {
      return { valid: false, error: 'Edit commands must have at least one operation' };
    }
  }

  if (command.type === 'create') {
    const createCmd = command as CreateCommand;
    if (!createCmd.mmoType) {
      return { valid: false, error: 'Create command requires mmoType' };
    }
  }

  if (command.type === 'reorder') {
    const reorderCmd = command as ReorderCommand;
    const validDirections = ['front', 'back', 'forward', 'backward'];
    if (!validDirections.includes(reorderCmd.direction)) {
      return { valid: false, error: `Invalid reorder direction: ${reorderCmd.direction}` };
    }
  }

  return { valid: true };
}

/**
 * Convert CommandColor to Color model
 */
export function commandColorToColor(cmdColor: CommandColor): Color {
  return new Color(cmdColor.r, cmdColor.g, cmdColor.b, cmdColor.a);
}

/**
 * Convert Color model to CommandColor
 */
export function colorToCommandColor(color: Color): CommandColor {
  return {
    r: color.r,
    g: color.g,
    b: color.b,
    a: color.a,
  };
}
